import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useState, useRef, useEffect } from "react";

// Optimized image component with lazy loading and aspect ratio handling
function ProductImage({ src, alt }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const imgRef = useRef(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Load the actual image
            const actualImg = new Image();
            actualImg.src = src;
            actualImg.onload = () => {
              if (img) {
                img.src = src;
                setLoaded(true);
              }
            };
            actualImg.onerror = () => setError(true);
            observer.unobserve(img);
          }
        });
      },
      { rootMargin: '100px', threshold: 0.1 }
    );

    observer.observe(img);
    return () => observer.disconnect();
  }, [src]);

  return (
    <div className="relative aspect-square overflow-hidden bg-[#0a0f1a]">
      {/* Placeholder skeleton */}
      {!loaded && !error && (
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] to-[#0a0f1a] animate-pulse" />
      )}
      
      {/* Actual image */}
      <img
        ref={imgRef}
        alt={alt}
        className={`w-full h-full object-contain p-4 transition-opacity duration-300 ${
          loaded ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ 
          objectFit: 'contain',
          backgroundColor: '#0a0f1a'
        }}
      />
      
      {/* Error fallback */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#0a0f1a]">
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-2">
              <span className="text-lg text-white/30">📦</span>
            </div>
            <span className="text-[10px] text-white/30">{alt}</span>
          </div>
        </div>
      )}
      
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-transparent to-transparent opacity-40 pointer-events-none" />
    </div>
  );
}

export default function ProductCard({ product }) {
  return (
    <Link
      to={`/products/${product.id}`}
      data-testid={`product-card-${product.id}`}
      className="group relative overflow-hidden rounded-xl bg-white/[0.02] border border-white/[0.05] hover:border-[#2DD4BF]/30 transition-all duration-300 will-change-transform"
    >
      {/* Image with lazy loading */}
      <div className="relative">
        <ProductImage src={product.image} alt={product.name} />
        
        {/* Category badge */}
        <span className="absolute top-3 left-3 text-[9px] font-bold tracking-[0.1em] uppercase px-2 py-0.5 rounded-full bg-black/40 text-white/80 backdrop-blur-sm">
          {product.category}
        </span>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="text-sm font-bold text-white group-hover:text-[#2DD4BF] transition-colors duration-200 mb-0.5 truncate">
          {product.name}
        </h3>
        <p className="text-[10px] text-[#2DD4BF]/70 font-medium mb-1.5 truncate">{product.salt}</p>
        <p className="text-[11px] text-[#94A3B8] line-clamp-2 leading-relaxed mb-2">
          {product.description}
        </p>

        {/* View more */}
        <div className="flex items-center gap-1 text-[11px] text-[#94A3B8] group-hover:text-[#2DD4BF] transition-colors">
          <span>View Details</span>
          <ArrowRight size={11} className="transform group-hover:translate-x-0.5 transition-transform duration-200" />
        </div>
      </div>
    </Link>
  );
}
